import React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import CloseButton from './Common/CloseButton';

const MULTI_SELECT_MENU_PROPS = {
  PaperProps: {
    sx: {
      maxHeight: 360,
    }
  }
};

const renderScheduleSelectValue = (selected, options, placeholder) => {
  const values = Array.isArray(selected) ? selected : [];
  if (!values.length) {
    return (
      <Box component="span" sx={{ color: 'text.secondary' }}>
        {placeholder}
      </Box>
    );
  }

  const optionMap = new Map((options || []).map((option) => [String(option.value), option.label]));
  const labels = values
    .map((value) => optionMap.get(String(value)) ?? String(value))
    .filter(Boolean);

  if (!labels.length) {
    return (
      <Box component="span" sx={{ color: 'text.secondary' }}>
        {placeholder}
      </Box>
    );
  }

  return (
    <Box component="span" sx={{ whiteSpace: 'normal' }}>
      {labels.join(', ')}
    </Box>
  );
};

const ScheduleMultiSelect = ({
  id,
  label,
  value,
  onChange,
  options,
  placeholder,
  sx,
}) => {
  const selectedValues = Array.isArray(value) ? value : [];
  const selectedValueSet = new Set(selectedValues.map((item) => String(item)));

  return (
    <FormControl fullWidth size="small" sx={sx}>
      <InputLabel id={`${id}-label`} shrink>
        {label}
      </InputLabel>
      <Select
        labelId={`${id}-label`}
        multiple
        displayEmpty
        value={value}
        label={label}
        onChange={(event) => onChange(event.target.value)}
        renderValue={(selected) => renderScheduleSelectValue(selected, options, placeholder)}
        MenuProps={MULTI_SELECT_MENU_PROPS}
        sx={{
          '& .MuiSelect-select': {
            whiteSpace: 'normal',
          }
        }}
      >
        {(options || []).map((option) => (
          <MenuItem key={option.value} value={option.value}>
            <Checkbox checked={selectedValueSet.has(String(option.value))} />
            <ListItemText primary={option.label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const WorkflowFlowAutomationDialog = ({
  open,
  mode,
  flowName,
  triggerMode,
  frequencyValue,
  frequencyUnit,
  scheduleDayOfWeek,
  scheduleDayOfMonth,
  scheduleMonth,
  scheduleHour,
  scheduleMinute,
  scheduleTimezone,
  calendarWeekdayOptions,
  calendarDayOfMonthOptions,
  calendarMonthOptions,
  calendarHourOptions,
  calendarMinuteOptions,
  error,
  onClose,
  onSubmit,
  onFlowNameChange,
  onTriggerModeChange,
  onFrequencyValueChange,
  onFrequencyUnitChange,
  onScheduleDayOfWeekChange,
  onScheduleDayOfMonthChange,
  onScheduleMonthChange,
  onScheduleHourChange,
  onScheduleMinuteChange,
  t,
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="sm"
    fullWidth
    PaperProps={{ sx: { borderRadius: 3, boxShadow: 10 } }}
  >
    <DialogTitle sx={{
      bgcolor: 'primary.main',
      color: 'white',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      pr: 1
    }}>
      {mode === 'create'
        ? t('workflow.automation_dialog_create_title', 'Create Flow')
        : t('workflow.automation_dialog_edit_title', 'Flow Automation')}
      <CloseButton
        ariaLabel="close"
        onClick={onClose}
        sx={{ color: 'white' }}
      />
    </DialogTitle>
    <DialogContent sx={{ pt: 2 }}>
      {mode === 'create' && (
        <TextField
          fullWidth
          size="small"
          margin="normal"
          label={t('workflow.automation_flow_name_label', 'Flow name')}
          value={flowName}
          onChange={(event) => onFlowNameChange(event.target.value)}
        />
      )}

      <FormControl fullWidth size="small" sx={{ mt: 1, mb: 1.5 }}>
        <InputLabel id="flow-automation-mode-label">
          {t('workflow.automation_mode_label', 'Trigger mode')}
        </InputLabel>
        <Select
          labelId="flow-automation-mode-label"
          value={triggerMode}
          label={t('workflow.automation_mode_label', 'Trigger mode')}
          onChange={(event) => onTriggerModeChange(event.target.value)}
        >
          <MenuItem value="disabled">{t('workflow.automation_mode_disabled', 'Disabled')}</MenuItem>
          <MenuItem value="event">{t('workflow.automation_mode_event', 'On events change')}</MenuItem>
          <MenuItem value="time">{t('workflow.automation_mode_time', 'Time schedule')}</MenuItem>
          <MenuItem value="calendar">{t('workflow.automation_mode_calendar', 'Calendar schedule')}</MenuItem>
        </Select>
      </FormControl>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        {triggerMode === 'time'
          ? t('workflow.automation_mode_help_time', 'Run this flow on a fixed schedule.')
          : triggerMode === 'calendar'
            ? t('workflow.automation_mode_help_calendar', 'Run this flow when the selected calendar fields match.')
          : triggerMode === 'event'
            ? t('workflow.automation_mode_help_event', 'Run this flow when new events/projects are detected.')
            : t('workflow.automation_mode_help_disabled', 'Disable automatic trigger. This flow runs only when triggered manually or by another flow.')}
      </Typography>

      {triggerMode === 'time' && (
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 1 }}>
          <TextField
            type="number"
            size="small"
            label={t('workflow.automation_frequency_value_label', 'Every')}
            value={frequencyValue}
            onChange={(event) => onFrequencyValueChange(event.target.value)}
            inputProps={{ min: 1, step: 1 }}
            sx={{ flex: 1 }}
          />
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel id="flow-automation-unit-label">
              {t('workflow.automation_frequency_unit_label', 'Unit')}
            </InputLabel>
            <Select
              labelId="flow-automation-unit-label"
              value={frequencyUnit}
              label={t('workflow.automation_frequency_unit_label', 'Unit')}
              onChange={(event) => onFrequencyUnitChange(event.target.value)}
            >
              <MenuItem value="minutes">{t('workflow.automation_unit_minutes', 'minutes')}</MenuItem>
              <MenuItem value="hours">{t('workflow.automation_unit_hours', 'hours')}</MenuItem>
              <MenuItem value="days">{t('workflow.automation_unit_days', 'days')}</MenuItem>
            </Select>
          </FormControl>
        </Box>
      )}

      {triggerMode === 'calendar' && (
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            mb: 1,
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ gridColumn: '1 / -1', display: 'block', mt: -0.25 }}
          >
            {t('workflow.automation_calendar_help', 'Leave a field empty to match any value.')}
          </Typography>

          <ScheduleMultiSelect
            id="flow-automation-weekdays"
            label={t('workflow.automation_weekdays_label', 'Weekdays')}
            value={scheduleDayOfWeek}
            onChange={onScheduleDayOfWeekChange}
            options={calendarWeekdayOptions}
            placeholder={t('workflow.automation_calendar_any_value', 'Any')}
            sx={{ gridColumn: '1 / -1' }}
          />

          <ScheduleMultiSelect
            id="flow-automation-days-of-month"
            label={t('workflow.automation_days_of_month_label', 'Days of month')}
            value={scheduleDayOfMonth}
            onChange={onScheduleDayOfMonthChange}
            options={calendarDayOfMonthOptions}
            placeholder={t('workflow.automation_calendar_any_value', 'Any')}
          />

          <ScheduleMultiSelect
            id="flow-automation-months"
            label={t('workflow.automation_months_label', 'Months')}
            value={scheduleMonth}
            onChange={onScheduleMonthChange}
            options={calendarMonthOptions}
            placeholder={t('workflow.automation_calendar_any_value', 'Any')}
          />

          <ScheduleMultiSelect
            id="flow-automation-hours"
            label={t('workflow.automation_hours_label', 'Hours')}
            value={scheduleHour}
            onChange={onScheduleHourChange}
            options={calendarHourOptions}
            placeholder={t('workflow.automation_calendar_any_value', 'Any')}
          />

          <ScheduleMultiSelect
            id="flow-automation-minutes"
            label={t('workflow.automation_minutes_label', 'Minutes')}
            value={scheduleMinute}
            onChange={onScheduleMinuteChange}
            options={calendarMinuteOptions}
            placeholder={t('workflow.automation_calendar_any_value', 'Any')}
          />

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ gridColumn: '1 / -1', display: 'block' }}
          >
            {t('workflow.automation_calendar_timezone_help', 'Times are saved in {{timezone}}. Empty fields match any value.', {
              timezone: scheduleTimezone || 'UTC'
            })}
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {error}
        </Alert>
      )}
    </DialogContent>
    <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
      <Button onClick={onClose} variant="outlined">
        {t('common.cancel', 'Cancel')}
      </Button>
      <Button onClick={onSubmit} variant="contained">
        {mode === 'create'
          ? t('workflow.automation_create_submit', 'Create Flow')
          : t('workflow.automation_save_submit', 'Save Automation')}
      </Button>
    </DialogActions>
  </Dialog>
);

export default WorkflowFlowAutomationDialog;
